import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Copy, Ellipsis, Lock, Pencil, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import { formatError } from "helpers/formatError";
import { useRevenueOpportunityMutations } from "hooks/useRevenueOpportunity";
import { useRevenueOpportunityNavigation } from "routes/revenue_opportunity/useRevenueOpportunityNavigation";
import UserService from "services/User";

const EDIT_ACLS = [ACL_RELATIONSHIPS.revenueOpportunity.edit];
const CREATE_ACLS = [ACL_RELATIONSHIPS.revenueOpportunity.create];
const DELETE_ACLS = [ACL_RELATIONSHIPS.revenueOpportunity.delete];

export function RevenueOpportunitiesTableActions({
  original,
  deleteRevenueOpportunity,
}) {
  const isLocked = original.is_locked;
  const isAdmin = UserService.isAdmin();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { openEdit, openClone } = useRevenueOpportunityNavigation();
  const { setLocked } = useRevenueOpportunityMutations();

  const remove = useMutation({
    mutationFn: () => deleteRevenueOpportunity(original.id),
    onSuccess: () => {
      setIsDeleteOpen(false);
      toast.success("Revenue Opportunity deleted");
    },
    onError: error =>
      toast.error(
        formatError(error, "Could not delete the Revenue Opportunity")
      ),
  });

  const toggleLock = () =>
    setLocked.mutate(
      { id: original.id, locked: !isLocked },
      {
        onSuccess: () =>
          toast.success(
            isLocked
              ? "Revenue Opportunity unlocked"
              : "Revenue Opportunity locked"
          ),
        onError: error =>
          toast.error(formatError(error, "Could not change the lock state")),
      }
    );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Revenue Opportunity actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-auto">
          {!isLocked && (
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem onClick={() => openEdit(original.id)}>
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
          )}
          <Authorized permissions={CREATE_ACLS}>
            <DropdownMenuItem onClick={() => openClone(original.id)}>
              <Copy />
              Clone
            </DropdownMenuItem>
          </Authorized>
          {isAdmin && (
            <DropdownMenuItem onClick={toggleLock}>
              {isLocked ? <Unlock /> : <Lock />}
              {isLocked ? "Unlock" : "Lock"}
            </DropdownMenuItem>
          )}
          {!isLocked && (
            <Authorized permissions={DELETE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setIsDeleteOpen(true)}
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </Authorized>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete Revenue Opportunity?"
        description="Are you sure you want to delete this Revenue Opportunity?"
        onConfirm={() => remove.mutate()}
      />
    </>
  );
}
